<template>
    <div>
        <div
            class="flex flex-wrap items-end gap-4 rounded-md bg-gray-50 p-4 text-sm text-gray-700 md:items-center md:gap-6"
        >
            <div class="flex flex-col gap-1">
                <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                    Language
                </label>
                <select
                    v-model="languageFilter"
                    class="block w-40 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                >
                    <option value="all">All languages</option>
                    <option
                        v-for="language in languages"
                        :key="language"
                        :value="language"
                    >
                        {{ language }}
                    </option>
                </select>
            </div>
            <div class="flex flex-col gap-1">
                <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                    Start time
                </label>
                <select
                    v-model="timeFilter"
                    class="block w-48 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                >
                    <option value="all">Any upcoming date</option>
                    <option value="next7">Within 7 days</option>
                    <option value="next30">Within 30 days</option>
                    <option value="later">Later than 30 days</option>
                </select>
            </div>
            <div class="flex flex-col gap-1">
                <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                    Sort by
                </label>
                <select
                    v-model="sortOption"
                    class="block w-48 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                >
                    <option value="start-asc">Start time · Soonest first</option>
                    <option value="start-desc">Start time · Latest first</option>
                    <option value="language-asc">Language · A → Z</option>
                    <option value="language-desc">Language · Z → A</option>
                </select>
            </div>
        </div>

        <div class="mt-5 space-y-3">
            <div
                v-if="visibleWebinars.length === 0"
                class="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-600"
            >
                No webinars match the selected filters. Try adjusting your filters to see more results.
            </div>

            <div
                v-for="webinar in visibleWebinars"
                :key="webinar.id"
                class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition duration-150 hover:border-myflowPrimary/60"
            >
                <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div class="flex-1 space-y-2">
                        <div class="flex flex-wrap items-center gap-2">
                            <h4 class="text-base font-semibold text-gray-900">
                                {{ webinar.title }}
                            </h4>
                            <span
                                v-if="webinar.language_label"
                                class="inline-flex items-center rounded-full bg-myflowPrimary/10 px-2.5 py-1 text-xs font-semibold uppercase text-myflowPrimary"
                            >
                                {{ webinar.language_label }}
                            </span>
                        </div>
                        <div class="flex flex-wrap gap-3 text-xs text-gray-600">
                            <span v-if="webinar.start_display">
                                {{ webinar.start_display }}
                                <span class="uppercase">({{ displayTimezone }})</span>
                            </span>
                            <span v-if="webinar.duration_minutes">
                                · Runs {{ webinar.duration_minutes }} minutes
                            </span>
                            <span
                                v-if="webinar.page_url"
                                class="inline-flex items-center"
                            >
                                ·
                                <a
                                    :href="webinar.page_url"
                                    target="_blank"
                                    class="ml-1 text-xs mf-link"
                                >
                                    View event details
                                </a>
                            </span>
                        </div>
                    </div>
                    <div
                        v-if="webinar.share_url"
                        class="flex items-center justify-end md:pl-4"
                    >
                        <span class="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                            Shareable link
                        </span>
                    </div>
                </div>
                <div
                    v-if="webinar.share_url"
                    class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                    <input
                        :value="webinar.share_url"
                        class="w-full max-w-full truncate bg-transparent text-sm text-gray-700 focus:outline-none"
                        type="text"
                        readonly
                        @focus="selectInput"
                    >
                    <link-field
                        class="mt-2 flex flex-wrap gap-3"
                        :url="webinar.share_url"
                        copy-label="Copy webinar link"
                    ></link-field>
                    <p class="mt-2 text-xs text-gray-500">
                        Registrations through this link stay linked to your account automatically.
                    </p>
                </div>
            </div>
        </div>

        <div
            v-if="showControls"
            class="mt-4 flex gap-3"
        >
            <button-base
                v-if="canShowMore"
                size="sm"
                type="button"
                class="whitespace-nowrap"
                @click="showMore"
            >
                Show more webinars
            </button-base>
            <button-secondary
                v-if="canShowLess"
                size="sm"
                type="button"
                class="whitespace-nowrap"
                @click="showLess"
            >
                Collapse list
            </button-secondary>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const INITIAL_VISIBLE = 3;
const VISIBLE_INCREMENT = 3;

const props = defineProps({
    webinars: {
        type: Array,
        default: () => [],
    },
    userTimezone: {
        type: String,
        default: 'UTC',
    },
});

const languageFilter = ref('all');
const timeFilter = ref('all');
const sortOption = ref('start-asc');
const visibleCount = ref(INITIAL_VISIBLE);

const displayTimezone = computed(() => props.userTimezone || 'UTC');

const languages = computed(() => {
    const unique = new Set();

    props.webinars.forEach((webinar) => {
        if (webinar.language_label) {
            unique.add(webinar.language_label);
        } else if (webinar.language) {
            unique.add(String(webinar.language).toUpperCase());
        }
    });

    return Array.from(unique).sort((a, b) => a.localeCompare(b));
});

const addDays = (date, days) => {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
};

const getStartDate = (webinar) => {
    return webinar.utc_start_time ? new Date(webinar.utc_start_time) : null;
};

const compareDatesAsc = (a, b) => {
    const dateA = getStartDate(a);
    const dateB = getStartDate(b);

    if (!dateA && !dateB) {
        return 0;
    }

    if (!dateA) {
        return 1;
    }

    if (!dateB) {
        return -1;
    }

    return dateA - dateB;
};

const compareStringsAsc = (a, b) => {
    if (!a && !b) {
        return 0;
    }

    if (!a) {
        return 1;
    }

    if (!b) {
        return -1;
    }

    return String(a).localeCompare(String(b));
};

const filteredAndSorted = computed(() => {
    const now = new Date();
    const within7Days = addDays(now, 7);
    const within30Days = addDays(now, 30);

    let collection = props.webinars.slice();

    if (languageFilter.value !== 'all') {
        collection = collection.filter((webinar) => {
            const language = webinar.language_label
                ? webinar.language_label
                : webinar.language
                    ? String(webinar.language).toUpperCase()
                    : null;

            return language === languageFilter.value;
        });
    }

    collection = collection.filter((webinar) => {
        if (timeFilter.value === 'all') {
            return true;
        }

        const startDate = getStartDate(webinar);

        if (!startDate) {
            return true;
        }

        if (timeFilter.value === 'next7') {
            return startDate <= within7Days;
        }

        if (timeFilter.value === 'next30') {
            return startDate <= within30Days;
        }

        if (timeFilter.value === 'later') {
            return startDate > within30Days;
        }

        return true;
    });

    collection.sort((a, b) => {
        switch (sortOption.value) {
            case 'start-desc':
                return compareDatesAsc(b, a);
            case 'language-asc':
                return compareStringsAsc(
                    a.language_label || (a.language ? String(a.language).toUpperCase() : null),
                    b.language_label || (b.language ? String(b.language).toUpperCase() : null),
                );
            case 'language-desc':
                return compareStringsAsc(
                    b.language_label || (b.language ? String(b.language).toUpperCase() : null),
                    a.language_label || (a.language ? String(a.language).toUpperCase() : null),
                );
            case 'start-asc':
            default:
                return compareDatesAsc(a, b);
        }
    });

    return collection;
});

const visibleWebinars = computed(() => {
    if (filteredAndSorted.value.length <= INITIAL_VISIBLE) {
        return filteredAndSorted.value;
    }

    return filteredAndSorted.value.slice(0, visibleCount.value);
});

const canShowMore = computed(() => filteredAndSorted.value.length > visibleCount.value);
const canShowLess = computed(
    () =>
        filteredAndSorted.value.length > INITIAL_VISIBLE &&
        visibleCount.value > INITIAL_VISIBLE,
);

const showMore = () => {
    visibleCount.value = Math.min(
        filteredAndSorted.value.length,
        visibleCount.value + VISIBLE_INCREMENT,
    );
};

const showLess = () => {
    visibleCount.value = INITIAL_VISIBLE;
};

watch([languageFilter, timeFilter, sortOption], () => {
    visibleCount.value = INITIAL_VISIBLE;
});

const showControls = computed(
    () => filteredAndSorted.value.length > INITIAL_VISIBLE || canShowLess.value,
);

const selectInput = (event) => {
    event.target?.select?.();
};
</script>
